<template>
  <transition name="fade">
    <div
      class="mask"
      v-if="isShowLoginDialog || showSessionExpiredDialog"
      @click="handleClose"
    >
      <div v-if="isShowLoginDialog" class="login-dialog" @click.stop>
        <!-- 上方白色容器 -->
        <div class="content-container">
          <!-- 上方插画 -->
          <img
            class="welcome-image"
            src="@/assets/welcome.svg"
            alt="欢迎插画"
          />

          <!-- 下方绿色容器 -->
          <div class="green-container">
            <!-- 二维码区域 -->
            <template v-if="!isQrcodeExpired">
              <img
                v-if="qrcodeUrl"
                class="qrcode"
                :src="qrcodeUrl"
                alt="微信登录二维码"
              />
              <div v-else class="qrcode-loading"></div>
            </template>

            <!-- 二维码过期显示 -->
            <div v-else class="qrcode-expired">
              <p>二维码已过期</p>
              <button @click="refreshQrcode">点击刷新</button>
            </div>

            <!-- 主标题 -->
            <div class="title">{{ loginTitle }}</div>

            <!-- 副标题 -->
            <div class="subtitle">
              {{ loginSubtitle }}
            </div>
          </div>
        </div>

        <!-- 下方关闭按钮 -->
        <IconCloseDialog
          class="close-button"
          fillColor="#ffffff"
          @click="handleClose"
        />
      </div>

      <!-- 新增的选择界面 -->
      <div
        v-if="showSessionExpiredDialog"
        class="session-expired-dialog"
        @click.stop
      >
        <h2>登录已失效</h2>
        <p class="desc">您需要重新登录才能继续操作</p>
        <div class="button-group">
          <button class="primary-button" @click="handleReloginClick">
            去登录
          </button>
          <button class="secondary-button" @click="handleBackToHomeClick">
            返回首页
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import IconCloseDialog from "@/components/icons/IconCloseDialog.vue";
import { useLogin } from "@/composables/login/useLogin";
import Tracking from "@/model/tracking.js";

const route = useRoute();
const isHomePage = computed(() => route.name === "home");

const {
  state: {
    isShowLoginDialog,
    qrcodeUrl,
    isQrcodeExpired,
    showSessionExpiredDialog,
    loginTitle,
    loginSubtitle,
    loginSceneType,
    LoginSceneEnum,
  },
  action: {
    refreshQrcode,
    handleSessionExpired,
    handleRelogin,
    handleBackToHome,
  },
} = useLogin();

// 监听登录弹窗显示状态，在实际显示时才发送曝光埋点
watch(isShowLoginDialog, (newVal) => {
  if (newVal) {
    Tracking.send({
      trigger: "dialog-view",
      page: "main-page",
      dialog: "login-dialog",
      scene:
        loginSceneType.value === LoginSceneEnum.UPLOAD ? "upload" : "normal",
    });
  }
});

// 监听会话过期弹窗显示状态
watch(showSessionExpiredDialog, (newVal) => {
  if (newVal) {
    Tracking.send({
      trigger: "dialog-view",
      page: "main-page",
      dialog: "session-expired-dialog",
    });
  }
});

const handleClose = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "login-dialog",
    object: "close-btn",
    scene: loginSceneType.value === LoginSceneEnum.UPLOAD ? "upload" : "normal",
  });
  handleSessionExpired(isHomePage.value);
};

const handleReloginClick = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "session-expired-dialog",
    object: "relogin-btn",
  });
  handleRelogin();
};

const handleBackToHomeClick = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    dialog: "session-expired-dialog",
    object: "back-to-home-btn",
  });
  handleBackToHome();
};
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;

  display: flex;
  justify-content: center;
  align-items: center;

  .login-dialog {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;

    .content-container {
      width: px2rem(364);
      height: px2rem(605);
      background: white;
      border-radius: px2rem(67);
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;

      .welcome-image {
        width: px2rem(347);
        height: px2rem(356);
        margin-top: 0;
      }

      .green-container {
        width: px2rem(344);
        height: px2rem(299);
        background: #359969;
        border-radius: px2rem(62);
        position: absolute;
        bottom: px2rem(10);
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        border: px2rem(3) solid #ffffff;
        box-sizing: border-box;

        .qrcode {
          width: px2rem(144);
          height: px2rem(144);
          margin-top: px2rem(36);
          border-radius: px2rem(10);
        }

        .title {
          margin-top: px2rem(20);
          font-size: px2rem(20);
          font-family: "思源黑体 Bold";
          color: #ffffff;
        }

        .subtitle {
          margin-top: px2rem(10);
          font-size: px2rem(14);
          font-family: "思源黑体 Light";
          color: #ffffff;
          text-align: center;
          width: px2rem(283);
          opacity: 0.5;
        }

        // 二维码加载状态（骨架屏）
        .qrcode-loading {
          width: px2rem(144);
          height: px2rem(144);
          margin-top: px2rem(36);
          background: rgba(255, 255, 255, 0.1);
          border-radius: px2rem(10);
          position: relative;
          overflow: hidden;

          &::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              90deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.1) 50%,
              rgba(255, 255, 255, 0) 100%
            );
            animation: shimmer 1.5s infinite;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        // 二维码过期状态
        .qrcode-expired {
          width: px2rem(144);
          height: px2rem(144);
          margin-top: px2rem(36);
          background: rgba(255, 255, 255, 0.1);
          border-radius: px2rem(10);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: px2rem(12);

          p {
            color: rgba(255, 255, 255, 0.8);
            font-family: "思源黑体 Light";
            font-size: px2rem(14);
            margin: 0;
          }

          button {
            background: transparent;
            border: px2rem(1) solid rgba(255, 255, 255, 0.6);
            border-radius: px2rem(6);
            color: rgba(255, 255, 255, 0.8);
            padding: px2rem(6) px2rem(12);
            cursor: pointer;
            font-family: "思源黑体 Light";
            font-size: px2rem(12);
            transition: all 0.3s ease;

            &:hover {
              background: rgba(255, 255, 255, 0.1);
              border-color: rgba(255, 255, 255, 0.8);
            }
          }
        }
      }
    }

    .close-button {
      margin-top: px2rem(20);
      width: px2rem(25);
      height: px2rem(25);
      cursor: pointer;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.1);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

.session-expired-dialog {
  position: relative;
  background: #262626;
  padding: px2rem(12) px2rem(26) px2rem(26);
  border-radius: px2rem(12);
  text-align: center;
  box-shadow: 0 px2rem(4) px2rem(8) rgba(0, 0, 0, 0.16);

  h2 {
    color: #ffffff;
    font-size: px2rem(16);
    margin-bottom: px2rem(4);
    font-weight: normal;
  }

  .desc {
    color: rgba(255, 255, 255, 0.3);
    font-size: px2rem(14);
    font-weight: lighter;
    margin-bottom: px2rem(24);
  }

  .button-group {
    display: flex;
    flex-direction: column;
    gap: px2rem(12);

    button {
      width: px2rem(280);
      height: px2rem(40);
      border-radius: px2rem(20);
      cursor: pointer;
      font-size: px2rem(13);
      transition: all 0.3s;
      display: flex;
      align-items: center;
      justify-content: center;

      &.primary-button {
        background: #2d7f6f;
        color: white;
        border: none;

        &:hover {
          box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.5);
        }
      }

      &.secondary-button {
        background: transparent;
        border: px2rem(1) solid #2d7f6f;
        color: white;

        &:hover {
          background: rgba(45, 127, 111, 0.1);
        }
      }
    }
  }
}
</style>
